<template>
  <div v-if="isVisible" class="hardkey-debug-overlay">
    <div class="debug-header">HardKey Debug</div>
    <div class="layers-container">
      <div v-for="(layer, index) in layers" :key="index" class="layer-section">
        <div class="layer-title">Layer {{ index }}</div>
        <div class="keys-grid">
          <component v-for="keyInfo in getKeysForLayer(index)" :key="keyInfo.key" :is="keyInfo.component" :width="32"
            :height="24" />
          <div v-for="textKey in getTextKeysForLayer(index)" :key="textKey" class="text-key">
            {{ textKey }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { Vue, Options } from 'vue-class-component'
import { keyenceHardKeyEventManager, KeyenceHardKey, SHOW_HARDKEY_DEBUG } from '@/utils/keyenceHT/keyenceHardKeyEventHandler'
import KeyF1 from '@/components/svg/keyenceHT/KeyF1.vue'
import KeyF2 from '@/components/svg/keyenceHT/KeyF2.vue'
import KeyF3 from '@/components/svg/keyenceHT/KeyF3.vue'
import KeyF4 from '@/components/svg/keyenceHT/KeyF4.vue'
import KeyEnt from '@/components/svg/keyenceHT/KeyEnt.vue'
import KeyC from '@/components/svg/keyenceHT/KeyC.vue'
import Key0 from '@/components/svg/keyenceHT/Key0.vue'
import Key1 from '@/components/svg/keyenceHT/Key1.vue'
import Key2 from '@/components/svg/keyenceHT/Key2.vue'
import Key3 from '@/components/svg/keyenceHT/Key3.vue'
import Key4 from '@/components/svg/keyenceHT/Key4.vue'
import Key5 from '@/components/svg/keyenceHT/Key5.vue'
import Key6 from '@/components/svg/keyenceHT/Key6.vue'
import Key7 from '@/components/svg/keyenceHT/Key7.vue'
import Key8 from '@/components/svg/keyenceHT/Key8.vue'
import Key9 from '@/components/svg/keyenceHT/Key9.vue'
import KeyPeriod from '@/components/svg/keyenceHT/KeyPeriod.vue'
import KeyMinus from '@/components/svg/keyenceHT/KeyMinus.vue'
import KeyTab from '@/components/svg/keyenceHT/KeyTab.vue'

type VueComponent = typeof KeyF1 | typeof KeyF2 | typeof KeyF3 | typeof KeyF4 | typeof KeyEnt | typeof KeyC | typeof Key0 | typeof Key1 | typeof Key2 | typeof Key3 | typeof Key4 | typeof Key5 | typeof Key6 | typeof Key7 | typeof Key8 | typeof Key9 | typeof KeyPeriod | typeof KeyMinus | typeof KeyTab

interface Layer {
  handlers: HardKeyRegistration[]
}

interface HardKeyRegistration {
  componentId: symbol
  key: KeyenceHardKey
  handler: (event: KeyboardEvent) => void | Promise<void>
  preventDefault: boolean
  stopPropagation: boolean
  enabled: boolean
  timestamp: number
}

interface KeyInfo {
  key: string
  component: VueComponent
}

@Options({
  name: 'HardKeyDebugOverlay',
  components: {
    KeyF1,
    KeyF2,
    KeyF3,
    KeyF4,
    KeyEnt,
    KeyC,
    Key0,
    Key1,
    Key2,
    Key3,
    Key4,
    Key5,
    Key6,
    Key7,
    Key8,
    Key9,
    KeyPeriod,
    KeyMinus,
    KeyTab,
  },
})
export default class HardKeyDebugOverlay extends Vue {
  private updateInterval: ReturnType<typeof setInterval> | null = null
  private updateTrigger = 0

  private keyComponentMap: Record<string, VueComponent> = {
    [KeyenceHardKey.F1]: KeyF1,
    [KeyenceHardKey.F2]: KeyF2,
    [KeyenceHardKey.F3]: KeyF3,
    [KeyenceHardKey.F4]: KeyF4,
    [KeyenceHardKey.ENTER]: KeyEnt,
    [KeyenceHardKey.BACKSPACE]: KeyC,
    [KeyenceHardKey.NUM_0]: Key0,
    [KeyenceHardKey.NUM_1]: Key1,
    [KeyenceHardKey.NUM_2]: Key2,
    [KeyenceHardKey.NUM_3]: Key3,
    [KeyenceHardKey.NUM_4]: Key4,
    [KeyenceHardKey.NUM_5]: Key5,
    [KeyenceHardKey.NUM_6]: Key6,
    [KeyenceHardKey.NUM_7]: Key7,
    [KeyenceHardKey.NUM_8]: Key8,
    [KeyenceHardKey.NUM_9]: Key9,
    [KeyenceHardKey.PERIOD]: KeyPeriod,
    [KeyenceHardKey.MINUS]: KeyMinus,
    [KeyenceHardKey.TAB]: KeyTab,
  }

  private textKeyMap: Record<string, string> = {
    [KeyenceHardKey.ARROW_UP]: '↑',
    [KeyenceHardKey.ARROW_DOWN]: '↓',
    [KeyenceHardKey.ARROW_LEFT]: '←',
    [KeyenceHardKey.ARROW_RIGHT]: '→',
  }

  get isVisible(): boolean {
    return SHOW_HARDKEY_DEBUG
  }

  get layers(): Layer[] {
    const updatedTrigger = this.updateTrigger
    const allLayers = keyenceHardKeyEventManager.getAllLayers()
    return allLayers.map(layer => ({
      handlers: [...layer.handlers],
    }))
  }

  mounted() {
    if (this.isVisible) {
      this.updateInterval = setInterval(() => {
        this.updateTrigger++
      }, 100)
    }
  }

  beforeUnmount() {
    if (this.updateInterval) {
      clearInterval(this.updateInterval)
      this.updateInterval = null
    }
  }

  getKeysForLayer(layerIndex: number): KeyInfo[] {
    const currentLayers = this.layers
    const layer = currentLayers[layerIndex]
    if (!layer || !layer.handlers) return []

    const uniqueKeys = new Set<string>()
    const keyInfoArray: KeyInfo[] = []

    layer.handlers.forEach((handler: HardKeyRegistration) => {
      if (handler.enabled && this.keyComponentMap[handler.key] && !uniqueKeys.has(handler.key)) {
        uniqueKeys.add(handler.key)
        keyInfoArray.push({
          key: handler.key,
          component: this.keyComponentMap[handler.key],
        })
      }
    })

    return keyInfoArray
  }

  getTextKeysForLayer(layerIndex: number): string[] {
    const currentLayers = this.layers
    const layer = currentLayers[layerIndex]
    if (!layer || !layer.handlers) return []

    const uniqueKeys = new Set<string>()
    const textKeys: string[] = []

    layer.handlers.forEach((handler: HardKeyRegistration) => {
      if (handler.enabled && this.textKeyMap[handler.key] && !uniqueKeys.has(handler.key)) {
        uniqueKeys.add(handler.key)
        textKeys.push(this.textKeyMap[handler.key])
      }
    })

    return textKeys
  }
}
</script>

<style scoped lang="scss">
.hardkey-debug-overlay {
  position: fixed;
  top: 10px;
  right: 50px;
  background: rgba(0, 0, 0, 0.85);
  color: #fff;
  padding: 10px;
  border-radius: 8px;
  z-index: 99999;
  font-family: 'Courier New', monospace;
  font-size: 12px;
  max-width: 300px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.5);
}

.debug-header {
  font-weight: bold;
  font-size: 14px;
  margin-bottom: 10px;
  padding-bottom: 8px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.3);
  text-align: center;
}

.layers-container {
  max-height: 70vh;
  overflow-y: auto;
}

.layer-section {
  margin-bottom: 12px;
  padding: 8px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 4px;
}

.layer-title {
  font-weight: bold;
  margin-bottom: 6px;
  color: #4fc3f7;
  font-size: 11px;
}

.keys-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  align-items: center;
}

.text-key {
  background: rgba(74, 74, 74, 0.8);
  border: 2px solid #1a1a1a;
  border-radius: 4px;
  width: 32px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  font-weight: bold;
  color: #fff;
}
</style>
